// Title screen — the painted Nazareth vista, the storybook title, the subtitle,
// "Presented by CatholicShop.com", and New Game / Continue. Music begins on first input.
import { Scene, type Draw, type BitmapFont, type Preload } from '../../engine/webgpu.js';
import { ART, MUSIC, C, CHARSET, FONT_SERIF } from '../config.js';
import { newGame, hasProgress } from '../session.js';
import { play as sfx } from '../audio.js';

export class Title extends Scene {
  private fTitle!: BitmapFont;
  private fH2!: BitmapFont;
  private fBody!: BitmapFont;
  private fSmall!: BitmapFont;
  private vista = -1;
  private started = false;
  private sel = 0;
  private options: string[] = [];
  private t = 0;

  override preload(load: Preload): void {
    load.image(ART.titleVista);
  }

  override setup(): void {
    this.fTitle = this.game.assets.font({ font: `bold 72px ${FONT_SERIF}`, charset: CHARSET });
    this.fH2 = this.game.assets.font({ font: `bold 30px ${FONT_SERIF}`, charset: CHARSET });
    this.fBody = this.game.assets.font({ font: `22px ${FONT_SERIF}`, charset: CHARSET });
    this.fSmall = this.game.assets.font({ font: `italic 18px ${FONT_SERIF}`, charset: CHARSET });
    try { this.vista = this.game.assets.framesOf(ART.titleVista); } catch { this.vista = -1; }
    this.options = hasProgress() ? ['Continue', 'New Game'] : ['Begin'];
    this.sel = 0;
    this.input.bind({
      up: ['ArrowUp', 'KeyW'], down: ['ArrowDown', 'KeyS'],
      choose: ['Space', 'Enter', 'KeyX'],
    });
  }

  private beginMusic(): void {
    if (this.started) return;
    this.started = true;
    this.sound.music(MUSIC.theme, { loop: true, volume: 0.5, fadeIn: 1.5 });
  }

  override update(dt: number): void {
    super.update(dt);
    this.t += dt;
    const k = this.input.keys;
    if (k.up.pressed || k.down.pressed) {
      this.beginMusic();
      this.sel = (this.sel + (k.down.pressed ? 1 : this.options.length - 1)) % this.options.length;
      sfx(this, 'select');
    }
    if (k.choose.pressed) {
      this.beginMusic();
      sfx(this, 'chime');
      const opt = this.options[this.sel];
      if (opt === 'New Game' || opt === 'Begin') newGame();
      this.gotoPlay({ fresh: opt !== 'Continue' });
    }
  }

  /** Small text over a painted vista has no contrast of its own. A ring of
   *  offset dark copies gives it a soft halo — cheaper to read than a scrim,
   *  and it does not box the text in against the artwork. */
  private haloText(d: Draw, font: BitmapFont, str: string, x: number, y: number,
                   color: string, alpha = 1, r = 2.6): void {
    for (let i = 0; i < 8; i++) {
      const a = (i / 8) * Math.PI * 2;
      d.text(font, str, x + Math.cos(a) * r, y + Math.sin(a) * r,
        { align: 'center', color: C.dusk, alpha: alpha * 0.36 });
    }
    d.text(font, str, x, y, { align: 'center', color, alpha });
  }

  override drawHud(d: Draw): void {
    const w = d.w, h = d.h;
    if (this.vista >= 0) {
      const fs = this.game.assets.frameSize(this.vista);
      const scale = Math.max(w / fs.w, h / fs.h);
      const dw = fs.w * scale, dh = fs.h * scale;
      d.sprite(this.vista, (w - dw) / 2, (h - dh) / 2, { w: dw, h: dh });
    } else {
      d.rect(0, 0, w, h, C.sky);
    }
    d.rect(0, 0, w, h, '#2c2a44', 0.12);
    d.rect(0, 0, w, h * 0.42, C.dusk, 0.28);

    const cx = w / 2;
    const ty = h * 0.24;
    // soft dark halo behind the title block for legibility
    d.circle(cx, ty + 70, w * 0.42, C.dusk, 0.16);
    d.circle(cx, ty + 70, w * 0.30, C.dusk, 0.18);
    d.circle(cx, ty + 70, w * 0.20, C.dusk, 0.18);
    d.text(this.fTitle, 'St. Joseph', cx, ty, { align: 'center', color: C.white });
    d.text(this.fTitle, 'the Carpenter', cx, ty + 76, { align: 'center', color: C.goldLight });
    d.text(this.fH2, 'Work  •  Pray  •  Provide', cx, ty + 168, { align: 'center', color: C.parchment });

    const my = h * 0.66;
    for (let i = 0; i < this.options.length; i++) {
      const on = i === this.sel;
      const yy = my + i * 46;
      const label = this.options[i];
      const tw = this.fBody.measure(label) + 60;
      if (on) {
        d.panel(cx - tw / 2, yy - 6, tw, 40, 'glossy');
        d.rect(cx - tw / 2, yy - 6, tw, 40, C.gold, 0.16);
      }
      d.text(this.fBody, label, cx, yy + 2, { align: 'center', color: on ? C.white : C.parchment, scale: on ? 1.05 : 1 });
    }
    // floor raised: at the bottom of its old swing this pulsed down to 0.3,
    // which over the busy vista was simply not there.
    const pulse = 0.78 + Math.sin(this.t * 3) * 0.18;
    this.haloText(d, this.fSmall, 'Arrow keys to choose · X or Enter to start',
      cx, my + this.options.length * 46 + 24, C.white, pulse);

    this.haloText(d, this.fSmall, 'Presented by CatholicShop.com', cx, h - 30, C.goldLight, 0.95);
  }
}
